import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import { Box, Typography } from '@material-ui/core';
import { BaseModal } from 'components/UI';
import { useStyles } from './common.styles';
import { FETCH_STATE } from 'constants/common';
import { AccessDeviceActions } from 'state/modules/accessDevice';
import { UserGroupsSelect, UsersSelect } from 'components/AsyncFields';

const ModalState = Object.freeze({
  ASSIGN: Symbol('ASSIGN'),
  UNASSIGN: Symbol('UNASSIGN'),
  CONFIRMATION: Symbol('CONFIRMATION'),
});

const AssignNFCCardModal = ({ id, open, handleClick }) => {
  const classes = useStyles();
  const formRef = useRef(null);
  const dispatch = useDispatch();

  const [selectedUserGroup, setSelectedUserGroup] = useState();

  const [currentState, setCurrentState] = useState(ModalState.ASSIGN);

  const { accessDevice, fetchingAccessDevice, assigningAccessDevice, unassigningAccessDevice } =
    useSelector((state) => state.accessDevice);

  const NFCCardSchema = Yup.object().shape({
    access_device_id: Yup.string().required(i18n.t('common.required_field')),
  });

  const initialValues = {
    access_device_id: accessDevice?.id,
    user_id: accessDevice?.user?.id || null,
  };

  useEffect(() => {
    setCurrentState(ModalState.ASSIGN);
  }, [open]);

  useEffect(() => {
    if (id && accessDevice?.id !== id && open) {
      dispatch(AccessDeviceActions.fetchAccessDeviceById(id));
    }
  }, [id, dispatch, open, accessDevice?.id]);

  const onSubmit = (values) => {
    if (values.user_id) {
      dispatch(AccessDeviceActions.assignAccessDevice(values));
      setSelectedUserGroup(null);
    } else {
      setCurrentState(ModalState.CONFIRMATION);
    }
  };

  const handleSubmit = () => {
    switch (currentState) {
      case ModalState.ASSIGN:
        formRef.current.submitForm();
        break;
      case ModalState.UNASSIGN:
        if (accessDevice?.user?.id) {
          setSelectedUserGroup(null);
          dispatch(
            AccessDeviceActions.unassignAccessDevice({
              access_device_id: accessDevice?.id,
              user_id: accessDevice?.user?.id,
            })
          );
        } else {
          handleCancel();
        }
        break;
      case ModalState.CONFIRMATION:
        setCurrentState(ModalState.ASSIGN);
        handleClick();
        break;
      default:
        return;
    }
  };

  const handleCancel = () => {
    switch (currentState) {
      case ModalState.ASSIGN:
        if (isAssigned) {
          setCurrentState(ModalState.UNASSIGN);
        }
        break;
      case ModalState.UNASSIGN:
      case ModalState.CONFIRMATION:
        handleClose();
        break;
      default:
        return;
    }
  };

  const handleClose = () => {
    switch (currentState) {
      case ModalState.ASSIGN:
        setSelectedUserGroup(null);
        handleClick();
        break;
      case ModalState.UNASSIGN:
      case ModalState.CONFIRMATION:
        setCurrentState(ModalState.ASSIGN);
        break;
      default:
        return;
    }
  };

  const loading =
    fetchingAccessDevice === FETCH_STATE.LOADING ||
    assigningAccessDevice === FETCH_STATE.LOADING ||
    unassigningAccessDevice === FETCH_STATE.LOADING;

  const isAssigned = accessDevice?.user?.id;

  const getTitle = () => {
    switch (currentState) {
      case ModalState.ASSIGN:
        return i18n.t('nfc.assign_title');
      case ModalState.UNASSIGN:
      case ModalState.CONFIRMATION:
        return i18n.t('nfc.confirm_action');
      default:
        return '';
    }
  };

  const getCancelButtonLabel = () => {
    switch (currentState) {
      case ModalState.ASSIGN:
        return i18n.t('nfc.unassign_driver');
      case ModalState.UNASSIGN:
      case ModalState.CONFIRMATION:
        return i18n.t('common.cancel');
      default:
        return '';
    }
  };

  const getDisabledCancelButton = () => {
    switch (currentState) {
      case ModalState.ASSIGN:
        return !isAssigned;
      case ModalState.UNASSIGN:
      case ModalState.CONFIRMATION:
        return false;
      default:
        return false;
    }
  };

  const modalTitle = getTitle();
  const cancelButtonLabel = getCancelButtonLabel();
  const disabledCancelButton = getDisabledCancelButton();

  return (
    <BaseModal
      size="sm"
      open={open}
      maxWidth={550}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleCancel}
      enableCloseButton={true}
      handleClick={handleClose}
      title={modalTitle}
      cancelButtonLabel={cancelButtonLabel}
      disabledCancelButton={disabledCancelButton}
      enterButtonLabel={i18n.t('common.confirm')}
      loading={loading}
      data-cy="nfc-modal-assign"
    >
      {currentState === ModalState.ASSIGN && (
        <>
          <Box className={classes.assignDescriptions}>
            <p className={classes.assignNotes}>{i18n.t('nfc.assign_note')}</p>
            {accessDevice?.external_type && accessDevice?.external_type !== '' && (
              <span className={classes.assignDescription}>
                {i18n.t('nfc.external_type_description', {
                  external_type: accessDevice?.external_type || '--',
                })}
              </span>
            )}
            <span className={classes.assignDescription}>
              {i18n.t('nfc.serial_description', { serial: accessDevice?.device_id || '--' })}
            </span>
          </Box>
          <Formik
            enableReinitialize
            innerRef={formRef}
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={NFCCardSchema}
          >
            {({ handleSubmit, setFieldValue }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Box mt={1}>
                  <Field
                    form={{
                      setFieldValue: (_, value) => setSelectedUserGroup(value || undefined),
                    }}
                    field={{
                      name: 'user_group_id',
                      value: selectedUserGroup || undefined,
                    }}
                    onChange={() => {
                      setFieldValue('user_id', '');
                    }}
                    label={i18n.t('nfc.filter_by_user_group')}
                    data-cy="nfc-field-user-group"
                    component={UserGroupsSelect}
                  />
                </Box>
                <Box mt={1}>
                  {open ? (
                    <Field
                      required={false}
                      type="creators"
                      margin="none"
                      name="user_id"
                      id="usersSelect"
                      component={UsersSelect}
                      label={i18n.t('common.search_user')}
                      customParams={{
                        user_group_id: selectedUserGroup,
                      }}
                      data-cy="nfc-field-user"
                      disabled={!selectedUserGroup}
                    />
                  ) : null}
                </Box>
              </Form>
            )}
          </Formik>
        </>
      )}
      {currentState === ModalState.UNASSIGN && (
        <Box mt={1.5} data-cy="confirm-unassign-user">
          <Typography id="modal-title" variant="body2" gutterBottom>
            {i18n.t('nfc.unassign_confirm')}
          </Typography>
        </Box>
      )}
      {currentState === ModalState.CONFIRMATION && (
        <Box mt={1.5} data-cy="confirm-without-user">
          <Typography id="modal-title" variant="body2" gutterBottom>
            {i18n.t('nfc.assign_without_user')}
          </Typography>
        </Box>
      )}
    </BaseModal>
  );
};

AssignNFCCardModal.propTypes = {
  id: PropTypes.number,
  open: PropTypes.bool,
  handleClick: PropTypes.func,
};

export default AssignNFCCardModal;
